import { pickPaperKind } from './mapSources';
import { deriveStatus } from './status';
import type {
  HouseholdCategory,
  HouseholdItem,
  Recurrence,
} from './types';

const STORAGE_KEY = 'piai-manual-items';

export interface ManualItemInput {
  title: string;
  category: HouseholdCategory;
  dueAt: string; // ISO or yyyy-mm-dd
  amount?: number;
  currency?: string;
  recurrence?: Recurrence;
  notes?: string;
  needsAttention?: boolean;
}

function readAll(): HouseholdItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as HouseholdItem[];
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeAll(items: HouseholdItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function toIsoDate(value: string): string {
  if (!value) return new Date().toISOString();
  if (value.includes('T')) return new Date(value).toISOString();
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 9, 0, 0, 0).toISOString();
}

export function loadManualItems(): HouseholdItem[] {
  return readAll();
}

export function createManualItem(input: ManualItemInput): HouseholdItem {
  const dueAt = toIsoDate(input.dueAt);
  const category = input.category;
  const title = input.title.trim();
  const status = input.needsAttention
    ? 'needs_attention'
    : deriveStatus(dueAt);

  const item: HouseholdItem = {
    id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    category,
    status,
    dueAt,
    amount: input.amount,
    currency: input.currency || 'AUD',
    recurrence: input.recurrence && input.recurrence !== 'once' ? input.recurrence : undefined,
    source: 'manual',
    paperKind: pickPaperKind(title, category),
    notes: input.notes?.trim() || undefined,
    detection: {
      summary: 'Added by you for this household.',
      expectedRecurrence:
        input.recurrence && input.recurrence !== 'once' ? input.recurrence : undefined,
      confidence: 1,
      foundIn: 'Manual entry',
    },
  };

  const all = readAll();
  all.push(item);
  writeAll(all);
  return item;
}

export function removeManualItem(id: string): void {
  writeAll(readAll().filter((i) => i.id !== id));
}

export function updateManualItem(
  id: string,
  patch: Partial<HouseholdItem>
): HouseholdItem | null {
  const all = readAll();
  const idx = all.findIndex((i) => i.id === id);
  if (idx < 0) return null;
  all[idx] = { ...all[idx], ...patch };
  writeAll(all);
  return all[idx];
}
